import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const bumppyUrl = "https://bumppy.in/packky/api/v1/uploadImage.php";

export default function useStore() {
  const { api, bearerApi, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const parseApiImages = (imageValue) => {
    try {
      if (!imageValue) return [];
      if (Array.isArray(imageValue)) return imageValue;

      if (typeof imageValue === "string") {
        const parsed = JSON.parse(imageValue);
        return Array.isArray(parsed) ? parsed : [];
      }

      return [];
    } catch {
      return [];
    }
  };

  const fetchStoreItemsApi = async (setItems, setFilteredItems, setLoading) => {
    setLoading?.(true);

    try {
      const res = await api.post("/api/Store/v1.0/getPackkyStoreItems", {
        userId: String(AdminId),
      });

      if (res?.data?.responseCode === "00") {
        const formatted = (res.data?.data || []).map((item) => ({
          ...item,
          imageList: parseApiImages(item.image),
        }));

        setItems?.(formatted);
        setFilteredItems?.(formatted);

        return formatted;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch store items"
        );
      }

      setItems?.([]);
      setFilteredItems?.([]);
      return [];
    } catch (error) {
      console.error("fetchStoreItemsApi error:", error);
      toast.error("Error fetching store items");
      setItems?.([]);
      setFilteredItems?.([]);
      return [];
    } finally {
      setLoading?.(false);
    }
  };

  const fetchStoreCategoriesApi = async (setCategories, setCategoryLoading) => {
    setCategoryLoading?.(true);

    try {
      const res = await api.post("/api/Store/v1.0/getAllItemCategory", {
        categoryId: "",
      });

      if (res?.data?.responseCode === "00") {
        const list = res.data?.data || [];
        setCategories?.(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch categories"
        );
      }

      setCategories?.([]);
      return [];
    } catch (error) {
      console.error("fetchStoreCategoriesApi error:", error);
      toast.error("Error fetching categories");
      setCategories?.([]);
      return [];
    } finally {
      setCategoryLoading?.(false);
    }
  };

  const addStoreCategoryApi = async (categoryName, onSuccess) => {
    try {
      const res = await bearerApi.post("/api/Store/v1.0/addPackkyStoreCategory", {
        categoryName,
      });

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Category added successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add category");
      }

      return false;
    } catch (error) {
      console.error("addStoreCategoryApi error:", error);
      toast.error("Error adding category");
      return false;
    }
  };

  const deleteStoreCategoryApi = async (categoryId, onSuccess) => {
    if (!categoryId) {
      toast.error("Invalid category ID");
      return false;
    }

    try {
      const res = await bearerApi.post(
        "/api/Store/v1.0/deletePackkyStoreCategory",
        {
          categoryId: String(categoryId),
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Category deleted successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete category");
      }

      return false;
    } catch (error) {
      console.error("deleteStoreCategoryApi error:", error);
      toast.error("Error deleting category");
      return false;
    }
  };

  const uploadStoreImageApi = async ({ fileName, base64String }) => {
    try {
      const payload = {
        userid: String(AdminId),
        item_name: fileName,
        path: "store",
        image_data: base64String,
        type: "IMG",
      };

      const res = await axios.post(bumppyUrl, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (String(res?.data?.status) === "0" && res?.data?.image_url) {
        return {
          success: true,
          imageUrl: res.data.image_url,
          imageName: res.data.image_name,
        };
      }

      return {
        success: false,
        message: res?.data?.msg || "Image upload failed",
      };
    } catch (error) {
      console.error("uploadStoreImageApi error:", error);

      return {
        success: false,
        message: "Error uploading image",
      };
    }
  };

  const saveStoreItemApi = async ({ itemData, onSuccess, onFinally }) => {
    try {
      const preparedImages = [];

      for (const img of itemData?.image || []) {
        if (typeof img === "string" && img.startsWith("http")) {
          preparedImages.push(img);
          continue;
        }

        if (img?.isBase64 && img?.base64) {
          const uploadRes = await uploadStoreImageApi({
            fileName: itemData.itemName || "store_item",
            base64String: img.base64,
          });

          if (!uploadRes.success) {
            toast.error(
              uploadRes.message || "Failed to upload one of the images"
            );
            onFinally?.();
            return false;
          }

          preparedImages.push(uploadRes.imageUrl);
        }
      }

      const payload = {
        items: [
          {
            ...(itemData.itemId ? { itemId: String(itemData.itemId) } : {}),
            itemName: itemData.itemName || "",
            category: itemData.category || "",
            size: itemData.size || "",
            description: itemData.description || "",
            isReturnable: itemData.isReturnable || "Yes",
            price: String(itemData.price || ""),
            image: preparedImages,
            color: itemData.color || "",
          },
        ],
      };

      const res = await bearerApi.post(
        "/api/Store/v1.0/insertPackkyStoreItems",
        payload
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          itemData.itemId
            ? "Store item updated successfully"
            : "Store item added successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to save store item");
      }

      return false;
    } catch (error) {
      console.error("saveStoreItemApi error:", error);
      toast.error("Error saving store item");
      return false;
    } finally {
      onFinally?.();
    }
  };

  return {
    fetchStoreItemsApi,
    fetchStoreCategoriesApi,
    addStoreCategoryApi,
    deleteStoreCategoryApi,
    uploadStoreImageApi,
    saveStoreItemApi,
  };
}